import { useDispatch, useSelector } from 'react-redux';
import serializeQueryParams from '../../../../utils/url/serialize-query-params';
import { AVAILABLE_PAYMENT_METHODS } from '../../payment.constants';
import cn from '../../../../utils/classnames';
import CardButton from '../controls/adyen-button/components/card-button/card-button';
import PaypalExpressGuest from '../controls/paypal-express/paypal-express-guest';
import PayPalPPCPControls from '../controls/paypal-ppcp-controls/paypal-ppcp-controls';
import AmazonPayBtn from '../controls/amazon-pay-button/amazon-pay-btn';
import ApplePayBtn from '../controls/adyen-button/components/apple-pay-button/apple-pay-button';
import GooglePayBtn from '../controls/adyen-button/components/google-pay-button/google-pay-button';
import KlarnaPayButton from '../controls/adyen-button/components/klarna-pay-button/klarna-pay-button';
import SepaPayButton from '../controls/adyen-button/components/sepa-pay-button/sepa-pay-button';
import IdealButton from '../controls/adyen-button/components/ideal-button/ideal-button';
import BancontactCardButton from '../controls/adyen-button/components/bancontact-card-button/bancontact-card-button';
import BancontactMobileButton
    from '../controls/adyen-button/components/bancontact-mobile-button/bancontact-mobile-button';
import Button from '../../../../ui/button/button';
import CartaDelDocenteButton from '../controls/carta-del-docente-button/carta-del-docente-button';
import { submitForm } from '../../reducers';
import formatPriceToFloat from '../../../../utils/price/format-price-to-float';
import translation from '../../../../languages';

type PayButtonProps = TranslationProps & {
    isDisabled: boolean;
};

const PayButton = ({ isDisabled, t }: PayButtonProps) => {
    const queryParams = serializeQueryParams();
    const dispatch = useDispatch();
    const { paymentCart, activePaymentOption } = useSelector((state: RootState) => state.payment);
    const finalPrice = formatPriceToFloat(paymentCart?.finalPrice ?? 0);

    const getPayButton = () => {
        if (!(finalPrice > 0)) {
            return (
                <Button
                    disabled={isDisabled}
                    variant={cn('btn-lg', {
                        white: isDisabled,
                        yellow: !isDisabled
                    })}
                    onClick={() => dispatch(submitForm())}
                >
                    {getDefaultButtonTitle()}
                </Button>
            );
        }

        // PP Express for guest user flow
        if (
            (activePaymentOption?.id === AVAILABLE_PAYMENT_METHODS.PayPalCheckout.id ||
            activePaymentOption?.id === AVAILABLE_PAYMENT_METHODS.PayPalCheckoutCreditCard.id ||
            activePaymentOption?.id === AVAILABLE_PAYMENT_METHODS.PayPalCheckoutSepa.id)
            && queryParams.ppTransactionId
        ) {
            return <PaypalExpressGuest />;
        }

        switch (activePaymentOption?.id) {
            case AVAILABLE_PAYMENT_METHODS.AdyenCard.id:
                return <CardButton />;
            case AVAILABLE_PAYMENT_METHODS.PayPalCheckout.id:
            case AVAILABLE_PAYMENT_METHODS.PayPalCheckoutCreditCard.id:
            case AVAILABLE_PAYMENT_METHODS.PayPalCheckoutSepa.id:
                return <PayPalPPCPControls />;
            case AVAILABLE_PAYMENT_METHODS.AmazonPay.id:
                return <AmazonPayBtn />;
            case AVAILABLE_PAYMENT_METHODS.ApplePay.id:
                return <ApplePayBtn />;
            case AVAILABLE_PAYMENT_METHODS.GooglePay.id:
                return <GooglePayBtn />;
            case AVAILABLE_PAYMENT_METHODS.KlarnaPayNow.id:
                return <KlarnaPayButton />;
            case AVAILABLE_PAYMENT_METHODS.SEPA.id:
                return <SepaPayButton />;
            case AVAILABLE_PAYMENT_METHODS.iDEAL.id:
                return <IdealButton />;
            case AVAILABLE_PAYMENT_METHODS.BancontactCard.id:
                return <BancontactCardButton />;
            case AVAILABLE_PAYMENT_METHODS.BancontactMobile.id:
                return <BancontactMobileButton />;
            case AVAILABLE_PAYMENT_METHODS.CartaDelDocente.id:
                return <CartaDelDocenteButton />;
            default:
                return (
                    <Button
                        disabled={isDisabled}
                        variant={cn('btn-lg', {
                            white: isDisabled,
                            yellow: !isDisabled
                        })}
                        onClick={() => dispatch(submitForm())}
                    >
                        {getDefaultButtonTitle()}
                    </Button>
                );
        };
    };

    const getDefaultButtonTitle = () => {
        if (finalPrice === 0) {
            return t('payment.freePurchase');
        }

        return activePaymentOption?.title
            ? t('payment.payType', { payType: activePaymentOption.title })
            : t('payment.payByInvoice');
    }

    return (
        <div className={cn('position-relative', { 'disabled-pay-button': isDisabled })}>
            {isDisabled && <div className='custom-blinder' />}
            {getPayButton()}
        </div>
    );
};

const translate = translation('payment');

export default translate(PayButton);
